/**
 * PhotoUpload — drag-drop + file picker + iOS camera capture component.
 *
 * Upload flow:
 *   1. User drops/selects/captures a photo
 *   2. Preview shown (URL.createObjectURL)
 *   3. On "Upload" click: validate → requestUploadUrl → uploadPhotoToS3 → pollPhotoReady
 *   4. On success: show two "Start Session" buttons → navigate to /session/new?photoId=<id>&mode=voice|text
 *
 * Constraints:
 * - Max file size: 20MB (T-02-75 browser-side validation)
 * - Allowed types: image/jpeg, image/png, image/heic, image/heif, image/webp
 * - iOS camera: <input type="file" accept="image/*" capture="environment" />
 * - HEIC preview may not work in all browsers — expected and noted
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { requestUploadUrl, uploadPhotoToS3, pollPhotoReady } from '../../lib/photoUpload';

const ALLOWED_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
]);

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20MB

type UploadState = 'idle' | 'uploading' | 'processing' | 'done' | 'error';

export function PhotoUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [uploadedPhotoId, setUploadedPhotoId] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Revoke object URL on unmount to avoid memory leaks
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const selectFile = useCallback((file: File) => {
    setError(null);
    setUploadState('idle');
    setUploadedPhotoId(null);

    // Validate file type
    if (!ALLOWED_TYPES.has(file.type) && file.type !== '') {
      setError(`File type not supported. Please use JPEG, PNG, HEIC, HEIF, or WebP.`);
      return;
    }

    // Validate file size (T-02-75)
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(`File too large. Maximum size is 20MB.`);
      return;
    }

    // Create object URL for preview
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreviewUrl(url);
    setSelectedFile(file);
  }, []);

  // Drag-and-drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) selectFile(file);
  }, [selectFile]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) selectFile(file);
    // Reset input so selecting the same file again triggers onChange
    e.target.value = '';
  }, [selectFile]);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setError(null);

    // Re-validate content type for server submission
    // For HEIC from iOS, type may be empty string — infer from extension
    let contentType = selectedFile.type;
    if (!contentType || contentType === 'application/octet-stream') {
      const ext = selectedFile.name.split('.').pop()?.toLowerCase() ?? '';
      const extMap: Record<string, string> = {
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        heic: 'image/heic',
        heif: 'image/heif',
        webp: 'image/webp',
      };
      contentType = extMap[ext] ?? 'image/jpeg';
    }

    if (!ALLOWED_TYPES.has(contentType)) {
      setError(`File type '${contentType}' is not supported.`);
      return;
    }

    try {
      setUploadState('uploading');

      // Step 1: Get presigned PUT URL from server
      const { uploadUrl, photoId } = await requestUploadUrl(selectedFile.name, contentType);

      // Step 2: Upload file directly to S3 (browser → S3, not through Lambda)
      await uploadPhotoToS3(uploadUrl, selectedFile);

      // Step 3: Poll until photo-processor Lambda writes the PHOTO# item
      setUploadState('processing');
      await pollPhotoReady(photoId);

      setUploadedPhotoId(photoId);
      setUploadState('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
      setUploadState('error');
    }
  };

  const handleStartSession = (mode: 'voice' | 'text') => {
    if (!uploadedPhotoId) return;
    navigate(`/session/new?photoId=${uploadedPhotoId}&mode=${mode}`);
  };

  const handleReset = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadState('idle');
    setError(null);
    setUploadedPhotoId(null);
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const isUploading = uploadState === 'uploading' || uploadState === 'processing';

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      {!selectedFile && (
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
            isDragOver
              ? 'border-amber-400 bg-amber-50'
              : 'border-stone-300 bg-stone-50 hover:border-stone-400'
          }`}
        >
          <div className="space-y-3">
            <div className="text-4xl" aria-hidden="true">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="mx-auto w-12 h-12 text-stone-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-stone-700 font-medium">Drop a photo here</p>
              <p className="text-stone-500 text-sm mt-1">JPEG, PNG, HEIC, WebP up to 20MB</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              {/* File picker */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 rounded-lg bg-stone-800 text-white text-sm font-medium hover:bg-stone-700 transition-colors"
              >
                Choose File
              </button>
              {/* iOS camera capture */}
              <button
                type="button"
                onClick={() => cameraInputRef.current?.click()}
                className="px-4 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium hover:bg-stone-100 transition-colors"
              >
                Take Photo
              </button>
            </div>
          </div>

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
            className="sr-only"
            onChange={handleFileChange}
            aria-label="Choose photo file"
          />
          {/* iOS camera capture (capture="environment" = rear camera) */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={handleFileChange}
            aria-label="Take photo with camera"
          />
        </div>
      )}

      {/* Preview + upload controls */}
      {selectedFile && uploadState !== 'done' && (
        <div className="space-y-4">
          {/* Preview */}
          {previewUrl && (
            <div className="relative">
              <img
                src={previewUrl}
                alt="Preview of selected photo"
                className="w-full max-h-64 object-contain rounded-xl border border-stone-200 bg-stone-100"
              />
              {!isUploading && (
                <button
                  type="button"
                  onClick={handleReset}
                  className="absolute top-2 right-2 bg-white rounded-full p-1.5 shadow-sm border border-stone-200 text-stone-500 hover:text-stone-800 transition-colors"
                  aria-label="Remove selected photo"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                  </svg>
                </button>
              )}
            </div>
          )}

          {/* Filename + size info */}
          <div className="text-sm text-stone-600">
            <span className="font-medium">{selectedFile.name}</span>
            <span className="ml-2 text-stone-400">
              ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
            </span>
          </div>

          {/* Status messages */}
          {uploadState === 'uploading' && (
            <div className="flex items-center gap-2 text-amber-700 text-sm">
              <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Uploading photo…
            </div>
          )}

          {uploadState === 'processing' && (
            <div className="flex items-center gap-2 text-amber-700 text-sm">
              <svg className="animate-spin w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Processing photo…
            </div>
          )}

          {error && (
            <div className="text-red-700 bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          {/* Upload button */}
          {(uploadState === 'idle' || uploadState === 'error') && (
            <button
              type="button"
              onClick={() => void handleUpload()}
              disabled={!selectedFile}
              className="w-full py-2.5 rounded-xl bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
            >
              Upload Photo
            </button>
          )}
        </div>
      )}

      {/* Success: session start buttons */}
      {uploadState === 'done' && uploadedPhotoId && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
            </svg>
            Photo uploaded! Start a memory session about it:
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={() => handleStartSession('voice')}
              className="flex-1 py-3 rounded-xl bg-amber-500 text-white font-semibold text-sm hover:bg-amber-600 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2"
            >
              Start Voice Session
            </button>
            <button
              type="button"
              onClick={() => handleStartSession('text')}
              className="flex-1 py-3 rounded-xl border border-stone-300 text-stone-700 font-semibold text-sm hover:bg-stone-50 transition-colors focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-2"
            >
              Start Text Session
            </button>
          </div>

          <button
            type="button"
            onClick={handleReset}
            className="w-full text-sm text-stone-500 hover:text-stone-700 transition-colors"
          >
            Upload another photo
          </button>
        </div>
      )}
    </div>
  );
}
